import { useState, useEffect, useMemo } from "react";
import { Search, Download, Plus, Image, Film, Layers, Play, X, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { PageFooter } from "@/components/PageFooter";
import { VideoThumbnail } from "@/components/VideoThumbnail";
import { externalApi } from "@/lib/api";
import { useLang } from "@/i18n";

const assetTypes = [
  { label: "All assets", key: "all" },
  { label: "Banners", key: "banner" },
  { label: "Videos", key: "video" },
  { label: "Pop-ups", key: "popup" },
];

const TYPE_ICON = {
  banner: Image,
  video: Film,
  popup: Layers,
};

const TYPE_LABEL = {
  banner: "Banner",
  video: "Video",
  popup: "Pop-up",
};

const isVideoMime = (mime) => typeof mime === "string" && mime.startsWith("video/");
const isImageMime = (mime) => typeof mime === "string" && mime.startsWith("image/");
const isPdfMime = (mime) => mime === "application/pdf";

const isVideoUrl = (url) => typeof url === "string" && /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url);
const isImageUrl = (url) => typeof url === "string" && /\.(jpe?g|png|gif|webp|avif|svg)(\?|$)/i.test(url);
const isPdfUrl = (url) => typeof url === "string" && /\.pdf(\?|$)/i.test(url);

const isVideo = (a) => isVideoMime(a?.mime) || isVideoUrl(a?.url);
const isImage = (a) => isImageMime(a?.mime) || isImageUrl(a?.url);
const isPdf = (a) => isPdfMime(a?.mime) || isPdfUrl(a?.url);
const canPreview = (a) => !!a?.url && (isVideo(a) || isImage(a) || isPdf(a));

const fileInfoFor = (a) => {
  if (a.mime) return a.mime.split("/").pop().toUpperCase();
  const ext = (a.name || "").split(".").pop();
  return ext ? ext.toUpperCase() : "FILE";
};

export const DigitalRepository = ({ onNavigate }) => {
  const { t } = useLang();
  const [activeType, setActiveType] = useState("all");
  const [activeCountry, setActiveCountry] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [preview, setPreview] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    externalApi
      .get("/cola-gallery")
      .then((res) => {
        if (cancelled) return;
        setItems(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "Failed to load gallery");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => {
      if (e.key === "Escape") setPreview(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [preview]);

  const countryFilters = useMemo(() => {
    const set = new Set();
    items.forEach((i) => (i.countries || []).forEach((c) => set.add(c)));
    return ["All", ...Array.from(set).sort()];
  }, [items]);

  const filtered = items.filter((a) => {
    const matchType = activeType === "all" || a.type === activeType;
    const matchCountry =
      activeCountry === "All" || (a.countries || []).includes(activeCountry);
    const matchSearch =
      searchQuery === "" ||
      (a.name || "").toLowerCase().includes(searchQuery.toLowerCase());
    return matchType && matchCountry && matchSearch;
  });

  return (
    <div data-testid="digital-repository-view">
      <div className="mb-10">
        <span className="cchbc-eyebrow mb-5">{t("Assets Library")}</span>
        <h1
          className="font-condensed text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-[#000000] mb-4 mt-4 leading-[0.95]"
          data-testid="repository-title"
        >
          {t("Digital repository")}
        </h1>
        <p className="text-[#555555] text-base leading-relaxed max-w-2xl">
          {t("Download and use approved brand assets for your market")}
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-10">
        <div className="lg:w-52 shrink-0" data-testid="asset-type-sidebar">
          <span className="cchbc-eyebrow mb-4">{t("Asset Type")}</span>
          <nav className="flex flex-wrap lg:flex-col gap-1 lg:space-y-0.5 mt-4">
            {assetTypes.map((at) => (
              <button
                key={at.key}
                onClick={() => setActiveType(at.key)}
                className={`w-full text-left px-3 py-2 text-sm rounded-sm transition-colors ${
                  activeType === at.key
                    ? "text-[#dd260e] font-semibold bg-[#dd260e]/5"
                    : "text-[#555555] hover:text-[#000000] hover:bg-[#fafafa]"
                }`}
                data-testid={`asset-type-${at.key}`}
              >
                {t(at.label)}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-4 mb-6 flex-wrap">
            <div className="relative flex-1 max-w-sm min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555555]" />
              <input
                type="text"
                placeholder={t("Search assets...")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#e8e8e8] rounded-sm text-sm text-[#000000] placeholder:text-[#555555]/60 focus:outline-none focus:border-[#dd260e] transition-colors"
                data-testid="asset-search-input"
              />
            </div>

            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs font-semibold text-[#555555] uppercase tracking-wider mr-1">
                {t("Country:")}
              </span>
              {countryFilters.map((c) => (
                <button
                  key={c}
                  onClick={() => setActiveCountry(c)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${
                    activeCountry === c
                      ? "bg-[#000000] text-white"
                      : "bg-[#e8e8e8] text-[#555555] hover:bg-[#000000] hover:text-white"
                  }`}
                  data-testid={`country-filter-${c.toLowerCase()}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {loading && (
            <div
              className="flex items-center justify-center py-20 text-[#555555]"
              data-testid="gallery-loading"
            >
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              <span className="text-sm">{t("Loading assets…")}</span>
            </div>
          )}

          {!loading && error && (
            <div
              className="border border-[#e8e8e8] rounded-sm py-10 px-4 text-center text-[#555555]"
              data-testid="gallery-error"
            >
              <p className="text-sm font-semibold text-[#000000] mb-1">
                {t("Couldn't load assets")}
              </p>
              <p className="text-xs">{error}</p>
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div
              className="border border-dashed border-[#e8e8e8] rounded-sm py-12 text-center text-[#555555]"
              data-testid="gallery-empty"
            >
              <p className="text-sm">{t("No Cola assets found for the current filters.")}</p>
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6"
              data-testid="asset-grid"
            >
              {filtered.map((asset) => {
                const Icon = TYPE_ICON[asset.type] || Image;
                const videoPreview = isVideo(asset) && asset.url ? asset.url : null;
                const poster = asset.thumbnail_url || (isImage(asset) ? asset.url : null);
                return (
                  <div
                    key={asset.uuid}
                    className="bg-white border border-[#e8e8e8] rounded-sm hover:border-[#dd260e] transition-colors group overflow-hidden"
                    data-testid={`asset-card-${asset.uuid}`}
                  >
                    {(poster || videoPreview) ? (
                      <button
                        type="button"
                        onClick={() => canPreview(asset) && setPreview(asset)}
                        className="h-36 relative overflow-hidden border-b border-[#e8e8e8] bg-black w-full block cursor-pointer"
                        aria-label={`Preview ${asset.name}`}
                        data-testid={`preview-asset-${asset.uuid}`}
                      >
                        {videoPreview ? (
                          <VideoThumbnail
                            src={videoPreview}
                            alt={asset.name}
                            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                          />
                        ) : (
                          <img
                            src={poster}
                            alt={asset.name}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                          />
                        )}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-[#dd260e] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                            {isVideo(asset) ? (
                              <Play
                                className="w-5 h-5 text-white ml-0.5"
                                strokeWidth={2.5}
                                fill="currentColor"
                              />
                            ) : (
                              <Icon className="w-5 h-5 text-white" strokeWidth={2} />
                            )}
                          </div>
                        </div>
                      </button>
                    ) : (
                      <div className="h-36 bg-[#fafafa] flex items-center justify-center border-b border-[#e8e8e8]">
                        <Icon className="w-10 h-10 text-[#555555]/40" strokeWidth={1.2} />
                      </div>
                    )}

                    <div className="p-4">
                      <p className="text-sm font-semibold text-[#000000] mb-2 group-hover:text-[#dd260e] transition-colors truncate">
                        {asset.name}
                      </p>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex gap-1.5 flex-wrap">
                          <Badge className="bg-[#dd260e]/10 text-[#dd260e] border-transparent rounded-sm text-[10px] font-semibold px-1.5 py-0 hover:bg-[#dd260e]/10">
                            {t(TYPE_LABEL[asset.type] || asset.type)}
                          </Badge>
                          {(asset.countries || []).map((c) => (
                            <Badge
                              key={c}
                              className="bg-[#e8e8e8] text-[#555555] border-transparent rounded-sm text-[10px] font-semibold px-1.5 py-0 hover:bg-[#e8e8e8]"
                            >
                              {c}
                            </Badge>
                          ))}
                        </div>
                        <span className="text-[10px] text-[#555555] shrink-0">
                          {fileInfoFor(asset)}
                        </span>
                      </div>
                    </div>

                    <div className="px-4 pb-3 flex gap-2">
                      {canPreview(asset) && (
                        <button
                          type="button"
                          onClick={() => setPreview(asset)}
                          className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-semibold text-[#000000] bg-[#fafafa] border border-[#e8e8e8] rounded-sm hover:border-[#000000] transition-colors"
                          data-testid={`preview-btn-${asset.uuid}`}
                        >
                          <Play className="w-3.5 h-3.5" fill="currentColor" />
                          {isVideo(asset) ? t("Watch") : t("Preview")}
                        </button>
                      )}
                      <a
                        href={asset.url}
                        download={asset.name}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-semibold text-[#555555] border border-[#e8e8e8] rounded-sm hover:border-[#dd260e] hover:text-[#dd260e] transition-colors"
                        data-testid={`download-asset-${asset.uuid}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                        {t("Download")}
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <button
            type="button"
            disabled
            title="Asset upload coming soon — admin only"
            className="w-full border border-dashed border-[#e8e8e8] rounded-sm py-4 text-sm text-[#555555]/50 font-medium flex items-center justify-center gap-2 cursor-not-allowed"
            data-testid="upload-asset-btn"
          >
            <Plus className="w-4 h-4" />
            {t("Upload new asset (admin only) — coming soon")}
          </button>
        </div>
      </div>

      <PageFooter />

      {preview && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 sm:p-8 animate-in fade-in-0 duration-150"
          onClick={() => setPreview(null)}
          data-testid="media-preview-modal"
          role="dialog"
          aria-modal="true"
          aria-label={preview.name}
        >
          <button
            onClick={() => setPreview(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
            data-testid="preview-close-btn"
            aria-label="Close preview"
          >
            <X className="w-5 h-5 text-white" />
          </button>

          <div className="absolute top-4 left-4 right-20 text-white z-10">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#dd260e] mb-1">
              {(preview.countries || []).join(" / ")} &middot; {fileInfoFor(preview)}
            </p>
            <p className="text-sm sm:text-base font-bold text-white truncate">{preview.name}</p>
          </div>

          <div
            className="relative max-w-[90vw] max-h-[80vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {isVideo(preview) && (
              <video
                src={preview.url}
                poster={preview.thumbnail_url || undefined}
                controls
                autoPlay
                playsInline
                className="max-w-[90vw] max-h-[80vh] bg-black rounded-sm shadow-2xl"
                data-testid="preview-video"
              />
            )}
            {isImage(preview) && (
              <img
                src={preview.url}
                alt={preview.name}
                className="max-w-[90vw] max-h-[80vh] object-contain rounded-sm shadow-2xl"
                data-testid="preview-image"
              />
            )}
            {isPdf(preview) && (
              <iframe
                src={preview.url}
                title={preview.name}
                className="w-[min(90vw,1100px)] h-[80vh] bg-white rounded-sm shadow-2xl border-0"
                data-testid="preview-pdf"
              />
            )}
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3">
            <a
              href={preview.url}
              download={preview.name}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#dd260e] hover:bg-white hover:text-[#000000] text-white text-xs font-bold uppercase tracking-[0.18em] rounded-sm transition-colors"
              data-testid="preview-download-btn"
            >
              <Download className="w-3.5 h-3.5" /> {t("Download")}
            </a>
          </div>
        </div>
      )}
    </div>
  );
};
